import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Satellite, ArrowRight } from "lucide-react";

export default function AuthPage() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name || email.split("@")[0]);
      toast.success("Signal locked");
      nav("/radar");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white grid md:grid-cols-2">
      <div className="relative hidden md:flex flex-col justify-between p-12 border-r border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px]">
          <div className="absolute inset-0 rounded-full border border-[#00E5FF]/20" />
          <div className="absolute inset-[15%] rounded-full border border-[#00E5FF]/20" />
          <div className="absolute inset-[30%] rounded-full border border-[#00E5FF]/20" />
          <div className="radar-sweep" />
        </div>
        <Link to="/" className="relative flex items-center gap-2">
          <Satellite size={18} className="text-[#00E5FF]" />
          <span className="font-[Unbounded] font-bold tracking-tighter">SIGNAL·RADAR</span>
        </Link>
        <div className="relative">
          <div className="text-xs tracking-[0.3em] font-mono text-[#00E5FF] mb-3">// PREFLIGHT</div>
          <p className="font-[Unbounded] text-3xl leading-tight tracking-tighter max-w-sm">
            Broadcast your signal.<br />Let the right humans find you.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-sm">
          <div className="text-xs tracking-[0.3em] font-mono text-[#00E5FF] mb-3">
            {mode === "login" ? "// LINK UP" : "// NEW SIGNAL"}
          </div>
          <h1 className="font-[Unbounded] text-3xl md:text-4xl font-bold tracking-tighter mb-8">
            {mode === "login" ? "Reconnect" : "Enter the grid"}
          </h1>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-[10px] tracking-[0.2em] font-mono text-white/50 uppercase">Display name</label>
                <input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full bg-[#0D0E15] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none transition-all"
                  placeholder="ex: nova.7"
                />
              </div>
            )}
            <div>
              <label className="text-[10px] tracking-[0.2em] font-mono text-white/50 uppercase">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-[#0D0E15] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none transition-all"
                placeholder="you@signal.io"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.2em] font-mono text-white/50 uppercase">Password</label>
              <input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-[#0D0E15] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none transition-all"
                placeholder="••••••••"
              />
            </div>

            <button
              data-testid="auth-submit-btn"
              disabled={busy}
              className="w-full mt-2 bg-[#00E5FF] text-black font-semibold py-3 rounded-lg hover:glow-primary disabled:opacity-50 transition-all inline-flex items-center justify-center gap-2"
            >
              {busy ? "Syncing…" : mode === "login" ? "Enter" : "Broadcast"}
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="mt-6 text-sm text-white/50">
            {mode === "login" ? (
              <>New signal? <button data-testid="switch-to-register-btn" onClick={() => setMode("register")} className="text-[#00E5FF] hover:underline">Create account</button></>
            ) : (
              <>Already broadcasting? <button data-testid="switch-to-login-btn" onClick={() => setMode("login")} className="text-[#00E5FF] hover:underline">Sign in</button></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
